import React, { useState, useEffect, useRef, useCallback } from "react";
import { Table, Spin, Select, Modal } from "antd";
import { EditOutlined, PlusOutlined, DeleteOutlined } from "@ant-design/icons";
import { getData, postData } from "../../config/api-connect";
import * as Styled from "./style.js";
import "./styles.css";
import { getSearchFilter, getDropdownFilter,getTimeSorter } from "../../utils/filterHelper.jsx";
import moment from "moment";
import Spinner from "../../components/spinner";
import ClockPicker from "../../components/clockPicker";

const { Option } = Select;

const StaffManagement = () => {
  let local_city = JSON.parse(localStorage.getItem("city_Id"));
  let local_selectedVenueId = JSON.parse(
    localStorage.getItem("selectedOffice"),
  );
  if (!local_selectedVenueId) local_selectedVenueId = "All";

  const [loading, setLoading] = useState(false);
  const [staffData, setStaffData] = useState([]);
  const [venues, setVenues] = useState([]);
  const [cities, setCities] = useState([]);
  const [cityId, setCityId] = useState(local_city);
  const [venuelist, setVenuelist] = useState([]);
  const [selectedVenueId, setSelectedVenueId] = useState(local_selectedVenueId);
  const [modalVisible, setModalVisible] = useState(false);
  const [modalLoading, setModalLoading] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [formData, setFormData] = useState({
    buddy_id: "",
    venue_id: "",
    check_in_time: "",
    check_out_time: "",
    department: "",
    mobile_no: "",
    buddy_title: "",
    reporting_manager: "",
  });
  const [formErrors, setFormErrors] = useState({});
  const [customerOptions, setCustomerOptions] = useState([]);
  const [customerSearching, setCustomerSearching] = useState(false);
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [departmentOptions, setDepartmentOptions] = useState([]);
  const [managerOptions, setManagerOptions] = useState([]);
  const [managerSearching, setManagerSearching] = useState(false);
  const debounceRef = useRef(null);

  const fetchStaffList = useCallback(async () => {
    setLoading(true);
    try {
      const response = await getData(
        `/business/get_staff_list/?venue_id=${selectedVenueId}&city_id=${cityId}`
      );
      setStaffData(response.results || []);
      if (response.department_options) {
        setDepartmentOptions(
          Object.entries(response.department_options).map(([value, label]) => ({ value, label }))
        );
      }
    } catch (error) {
      console.error("Error fetching staff list:", error);
    }
    setLoading(false);
  }, [selectedVenueId, cityId]);

  const searchManagers = useCallback((searchTerm) => {
    if (!searchTerm || searchTerm.trim().length < 3) {
      setManagerOptions([]);
      return;
    }

    setManagerSearching(true);

    setTimeout(async () => {
      try {
        const response = await postData(
          "/eagleeye/search_buddy/",
          { search: searchTerm.trim() }
        );

        setManagerOptions(response.results || []);
      } catch (error) {
        console.error(error);
      } finally {
        setManagerSearching(false);
      }
    }, 400);
  }, []);

  const getVenuesInCity = useCallback(() => {
    if (cities[0] && !cityId) setCityId([cities[0].id]);

    let filteredVenues;
    if (cityId === "All" || (Array.isArray(cityId) && cityId.includes("All"))) {
      filteredVenues = venues;
    } else {
      filteredVenues = Array.isArray(cityId)
        ? venues.filter((item) => cityId.includes(item.city))
        : venues;
    }

    setVenuelist(filteredVenues);
    const venueExists = filteredVenues.some(
      (item) => item.id === selectedVenueId,
    );
    if (!venueExists) setSelectedVenueId("All");
  }, [cities, cityId, venues, selectedVenueId]);

  useEffect(() => {
    localStorage.setItem("selectedOffice", JSON.stringify(selectedVenueId));
    localStorage.setItem("city_Id", JSON.stringify(cityId));
    if (cityId !== "") fetchStaffList();
  }, [selectedVenueId, cityId, fetchStaffList]);

  useEffect(() => {
    getVenues();
  }, []);

  useEffect(() => {
    if (cities.length) getVenuesInCity();
  }, [cities, cityId,getVenuesInCity]);

  const handleCityChange = (value) => {
    if (value.length > 0) {
      const last_selected = value[value.length - 1];
      if (last_selected === "All") setCityId(["All"]);
      else setCityId(value.filter((v) => v !== "All"));
    } else setCityId(["All"]);
  };

  const getVenues = async () => {
    try {
      const response = await getData(
        `/business/get_venues_for_buddies/?office_only=true`,
      );
      setVenues(response.result);
      setCities(response.cities);
    } catch (error) {
      console.log(error.message);
    }
  };

  const searchCustomers = useCallback((searchTerm) => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setCustomerSearchTerm(searchTerm || "");
    if (!searchTerm || searchTerm.trim().length < 3) {
      setCustomerOptions([]);
      return;
    }
    setCustomerSearching(true);
    debounceRef.current = setTimeout(async () => {
      try {
        const response = await getData(
          `/customer_list/search_staff/?search=${encodeURIComponent(searchTerm.trim())}`
        );
        if (response.success) {
          setCustomerOptions(response.results || []);
        }
      } catch (error) {
        console.error("Error searching customers:", error);
      } finally {
        setCustomerSearching(false);
      }
    }, 400);
  }, []);

  const openAddModal = () => {
    setEditingStaff(null);
    setFormData({
      buddy_id: "",
      venue_id: selectedVenueId !== "All" ? selectedVenueId : "",
      check_in_time: "",
      check_out_time: "",
      department: "",
      mobile_no: "",
    });
    setCustomerOptions([]);
    setFormErrors({});
    setModalVisible(true);
  };

  const openEditModal = (record) => {
    setEditingStaff(record);
    setFormData({
      buddy_id: record.buddy_id,
      venue_id: record.venue_id,
      check_in_time: record.check_in_time || "",
      check_out_time: record.check_out_time || "",
      department: record.department !== undefined ? String(record.department) : "",
      mobile_no: record.mobile_no || "",
      buddy_title: record.buddy_title || "",
      reporting_manager: record.reporting_manager?.id || "",
    });
    setCustomerOptions([{
      id: record.buddy_id,
      name: record.name,
      mobile_no: record.mobile_no,
    }]);
    if (record.reporting_manager) {
      setManagerOptions([
        {
          id: record.reporting_manager.id,
          name: record.reporting_manager.name,
          mobile_no: record.reporting_manager.mobile_no
        },
      ]);
    }
    setFormErrors({});
    setModalVisible(true);
  };

  const handleSaveStaff = async () => {
    const errors = {};
    if (!formData.buddy_id) errors.buddy_id = "Staff is required";
    if (!formData.venue_id) errors.venue_id = "Office is required";
    if (!formData.check_in_time) errors.check_in_time = "Check-in time is required";
    if (!formData.check_out_time) errors.check_out_time = "Check-out time is required";
    if (editingStaff && !formData.department) errors.department = "Department is required";

    if (formData.check_in_time && formData.check_out_time) {
      const inTime = moment(formData.check_in_time, ["hh:mm A", "h:mm A"]);
      const outTime = moment(formData.check_out_time, ["hh:mm A", "h:mm A"]);
      if (inTime.isValid() && outTime.isValid() && !outTime.isAfter(inTime)) {
        errors.check_out_time = "Check-out time must be after check-in time";
      }
    }

    if (Object.keys(errors).length) {
      setFormErrors(errors);
      return;
    }
    setFormErrors({});
    setModalLoading(true);
    try {
      // Helper to convert to 24hr format if needed
      const to24hr = (val) => {
        if (!val) return null;
        // If already 24hr (e.g. 18:00), just return
        if (/^\d{2}:\d{2}$/.test(val)) return val;
        // If 12hr with AM/PM
        const m = val.match(/(\d{1,2}):(\d{2})\s*(AM|PM)/i);
        if (m) {
          let h = parseInt(m[1], 10);
          const min = m[2];
          const ampm = m[3].toUpperCase();
          if (ampm === "PM" && h < 12) h += 12;
          if (ampm === "AM" && h === 12) h = 0;
          return `${String(h).padStart(2, "0")}:${min}`;
        }
        // fallback: return as is
        return val;
      };
      const payload = {
        buddy_id: formData.buddy_id,
        venue_id: formData.venue_id,
        check_in_time: to24hr(formData.check_in_time) || null,
        check_out_time: to24hr(formData.check_out_time) || null,
      };
      if (editingStaff && editingStaff.id) {
        payload.community_member_id = editingStaff.id;
        payload.department = formData.department;
        payload.mobile_no = formData.mobile_no;
        payload.buddy_title = formData.buddy_title;
        payload.reporting_manager = formData.reporting_manager;
      }
      await postData(`/business/add_or_edit_staff/`, payload);
      setModalVisible(false);
      fetchStaffList();
    } catch (error) {
      console.error("Error saving staff:", error);
    } finally {
      setModalLoading(false);
    }
  };

  const renderHeader = (title) => (
    <div
      style={{ textAlign: "center", fontWeight: "bold", textWrap: "nowrap" }}
    >
      {title}
    </div>
  );

  const handleDeleteStaff = (record) => {
    const staffName = record.name || 'this staff member';
    const officeName = record.venue_name || 'this office';
    Modal.confirm({
      title: 'Delete Staff',
      content: `Are you sure you want to remove ${staffName} from ${officeName}?`,
      okText: 'Yes, Remove',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        setLoading(true);
        try {
          await postData('/business/remove_staff_from_venue/', { community_member_id: record.id });
          fetchStaffList();
        } catch (error) {
          console.error('Error removing staff:', error);
        }
        setLoading(false);
      },
    });
  };

  const getDepartmentName = (id) => {
    const found = departmentOptions.find((opt) => String(opt.value) === String(id));
    return found ? found.label : '';
  };

  const columns = [
    {
      title: renderHeader("Name"),
      dataIndex: "name",
      key: "name",
      align: "center",
      ...getSearchFilter("name", "Name"),
    },
    {
      title: renderHeader("Mobile No"),
      dataIndex: "mobile_no",
      key: "mobile_no",
      align: "center",
      ...getSearchFilter("mobile_no", "Mobile No"),
    },
    {
      title: renderHeader("Title"),
      dataIndex: "buddy_title",
      key: "buddy_title",
      align: "center",
      ...getSearchFilter("buddy_title", "Title"),
    },
    {
      title: renderHeader("Department"),
      dataIndex: "department",
      key: "department",
      align: "center",
      render: (value) => getDepartmentName(value),
    },
    {
      title: renderHeader("Office"),
      dataIndex: "venue_name",
      key: "venue_name",
      align: "center",
      ...getDropdownFilter("venue_name", staffData),
    },
    {
      title: renderHeader("Manager"),
      dataIndex: "reporting_manager",
      key: "reporting_manager",
      align: "center",
      ...getSearchFilter("reporting_manager.name", "Reporting Manager"),
      render: value => value?.name || "-",
    },
    {
      title: renderHeader("Check-In Time"),
      dataIndex: "check_in_time",
      key: "check_in_time",
      ...getTimeSorter("check_in_time"),
      align: "center",
    },
    {
      title: renderHeader("Check-Out Time"),
      dataIndex: "check_out_time",
      key: "check_out_time",
      ...getTimeSorter("check_out_time"),
      align: "center",
    },
    {
      title: renderHeader("Actions"),
      key: "actions",
      align: "center",
      render: (_, record) => (
        <div style={{ display: 'flex', gap: 8, justifyContent: 'center' }}>
          <button
            className="btn btn-sm"
            style={{ background: '#fff', color: '#222', border: '1px solid #ccc', borderRadius: 6, display: 'flex', alignItems: 'center', padding: '4px 8px', gap: 2 }}
            onClick={() => openEditModal(record)}
          >
            <EditOutlined style={{ marginRight: 2 }} /> Edit
          </button>
          <button
            className="btn btn-sm"
            style={{ background: '#fff', color: '#ff2222', border: '1px solid #ccc', borderRadius: 6, display: 'flex', alignItems: 'center', padding: '4px 8px', gap: 2 }}
            onClick={() => handleDeleteStaff(record)}
          >
            <DeleteOutlined style={{ marginRight: 2 }} /> Delete
          </button>
        </div>
      ),
    },
  ];

  return (
    <Spin spinning={loading} indicator={<Spinner />}>
      <Styled.MainContainer className="buddyAttendance">
        <Styled.TopDiv>
          <Styled.SelectorContainer>
            {cities.length > 0 && (
              <Styled.Selector>
                <span className="indicatorText">City</span>
                <Select
                  mode="multiple"
                  className="mainSelector"
                  style={{ width: 150 }}
                  showSearch
                  placeholder="Search to Select"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.name.toLowerCase().includes(input.toLowerCase())
                  }
                  onChange={handleCityChange}
                  value={cityId}
                  bordered={false}
                >
                  <Option value="All" name="All">
                    All
                  </Option>
                  {cities.map((item) => (
                    <Option value={item.id} key={item.id} name={item.name}>
                      {item.name}
                    </Option>
                  ))}
                </Select>
              </Styled.Selector>
            )}

            {venuelist && venuelist.length > 0 && (
              <Styled.Selector>
                <span className="indicatorText">Office</span>
                <Select
                  className="mainSelector"
                  style={{ width: 300 }}
                  showSearch
                  placeholder="Search to Select"
                  optionFilterProp="children"
                  filterOption={(input, option) =>
                    option.name.toLowerCase().includes(input.toLowerCase())
                  }
                  onChange={setSelectedVenueId}
                  value={selectedVenueId}
                  bordered={false}
                >
                  <Option value="All" name="All">
                    All
                  </Option>
                  {venuelist.map((item) => (
                    <Option value={item.id} key={item.id} name={item.name}>
                      {item.name}
                    </Option>
                  ))}
                </Select>
              </Styled.Selector>
            )}
          </Styled.SelectorContainer>
        </Styled.TopDiv>

        <Styled.NewHeader>
          <span></span>
          <span> Staff Management </span>
          <button
            className="btn btn-sm downloadBtn"
            onClick={openAddModal}
          >
            <PlusOutlined style={{ marginRight: 4 }} /> Add Staff
          </button>
        </Styled.NewHeader>

        <div className="recordCount">{staffData.length || 0} records</div>
        <Table
          className="drill-down-table table_container"
          rowKey="id"
          columns={columns}
          dataSource={staffData}
          pagination={{ pageSize: 50, showSizeChanger: false }}
          scroll={{ x: "max-content" }}
        />

        <Modal
          title={editingStaff ? "Edit Staff" : "Add Staff"}
          open={modalVisible}
          onCancel={() => setModalVisible(false)}
          onOk={handleSaveStaff}
          confirmLoading={modalLoading}
          okText="Save"
        >
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div>
              <label>Staff</label>
              {editingStaff ? (
                <Select
                  style={{ width: "100%" }}
                  value={`${editingStaff.name} — ${editingStaff.mobile_no}`}
                  disabled
                />
              ) : (
                <Select
                  style={{ width: "100%" }}
                  showSearch
                  value={formData.buddy_id || undefined}
                  placeholder="Search by name or mobile"
                  filterOption={false}
                  onSearch={searchCustomers}
                  onChange={(value, option) => {
                    const selected = customerOptions.find((c) => c.id === value);
                    setFormData({
                      ...formData,
                      buddy_id: value,
                      mobile_no: selected ? selected.mobile_no : "",
                    });
                    setFormErrors({ ...formErrors, buddy_id: "" });
                  }}
                  loading={customerSearching}
                  notFoundContent={customerSearching ? "Searching..." : customerSearchTerm.trim().length >= 3 ? "No results found" : "Type at least 3 characters"}
                  status={formErrors.buddy_id ? "error" : ""}
                >
                  {customerOptions.map((c) => (
                    <Option key={c.id} value={c.id}>
                      {c.name} — {c.mobile_no}
                    </Option>
                  ))}
                </Select>
              )}
              {formErrors.buddy_id && <div style={{ color: "#ff4d4f", fontSize: 12 }}>{formErrors.buddy_id}</div>}
            </div>
            {editingStaff && (
              <>
                <div>
                  <label>Mobile No.</label>
                  <input
                    style={{ width: "100%", padding: 6, borderRadius: 4, border: '1px solid #d9d9d9', background: '#fff' }}
                    value={formData.mobile_no || editingStaff.mobile_no || ""}
                    onChange={e => setFormData({ ...formData, mobile_no: e.target.value })}
                  />
                </div>
                <div>
                  <label>Title</label>
                  <input
                    style={{ width: "100%", padding: 6, borderRadius: 4, border: '1px solid #d9d9d9', background: '#fff' }}
                    value={formData.buddy_title || editingStaff.buddy_title || ""}
                    onChange={e => setFormData({ ...formData, buddy_title: e.target.value })}
                  />
                </div>
                <div>
                  <label>Department</label>
                  <Select
                    style={{ width: "100%" }}
                    value={formData.department || undefined}
                    onChange={(value) => { setFormData({ ...formData, department: value }); setFormErrors({ ...formErrors, department: "" }); }}
                    placeholder="Select Department"
                    status={formErrors.department ? "error" : ""}
                  >
                    {departmentOptions.map((opt) => (
                      <Option value={opt.value} key={opt.value}>{opt.label}</Option>
                    ))}
                  </Select>
                  {formErrors.department && <div style={{ color: "#ff4d4f", fontSize: 12 }}>{formErrors.department}</div>}
                </div>
                <div>
                  <label>Reporting Manager</label>

                  <Select
                    style={{ width: "100%" }}
                    showSearch
                    value={formData.reporting_manager || undefined}
                    placeholder="Search reporting manager"
                    filterOption={false}
                    onSearch={searchManagers}
                    onChange={(value) =>
                      setFormData({
                        ...formData,
                        reporting_manager: value,
                      })
                    }
                    loading={managerSearching}
                    allowClear
                  >
                    {managerOptions.map((manager) => (
                      <Option key={manager.id} value={manager.id}>
                        {manager.name} — {manager.mobile_no}
                      </Option>
                    ))}
                  </Select>
                </div>
              </>
            )}
            <div>
              <label>Office</label>
              <Select
                style={{ width: "100%" }}
                value={formData.venue_id || undefined}
                onChange={(value) => { setFormData({ ...formData, venue_id: value }); setFormErrors({ ...formErrors, venue_id: "" }); }}
                placeholder="Select Office"
                status={formErrors.venue_id ? "error" : ""}
              >
                {venues.map((item) => (
                  <Option value={item.id} key={item.id}>
                    {item.name}
                  </Option>
                ))}
              </Select>
              {formErrors.venue_id && <div style={{ color: "#ff4d4f", fontSize: 12 }}>{formErrors.venue_id}</div>}
            </div>
            <div>
              <label>Check-In Time</label>
              <ClockPicker
                value={formData.check_in_time}
                onChange={(time) => { setFormData({ ...formData, check_in_time: time }); setFormErrors({ ...formErrors, check_in_time: "" }); }}
                placeholder="Select check-in time"
                error={!!formErrors.check_in_time}
              />
              {formErrors.check_in_time && <div style={{ color: "#ff4d4f", fontSize: 12 }}>{formErrors.check_in_time}</div>}
            </div>
            <div>
              <label>Check-Out Time</label>
              <ClockPicker
                value={formData.check_out_time}
                onChange={(time) => { setFormData({ ...formData, check_out_time: time }); setFormErrors({ ...formErrors, check_out_time: "" }); }}
                placeholder="Select check-out time"
                error={!!formErrors.check_out_time}
              />
              {formErrors.check_out_time && <div style={{ color: "#ff4d4f", fontSize: 12 }}>{formErrors.check_out_time}</div>}
            </div>
          </div>
        </Modal>
      </Styled.MainContainer>
    </Spin>
  );
};

export default StaffManagement;
